import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import Row from 'components/Common/Row';

import BoardContext from 'contexts/BoardContext';
import FlexCentered from 'components/Common/FlexCentered';
import Expectation, {
  RecordTaggingButton,
  OverrideTooltip,
} from 'components/DriverAX/components/Expectation';
import Loading from 'components/Loading/Loading';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import usePopup from 'hooks/usePopup';
import isPerformanceBoard from 'isPerformanceBoard';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Card from 'kingpin/atoms/Card';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import Tooltip from '../../../Tooltip';
import Dropdown from '../../../Inputs/Dropdown';
import useBonusEligibility from '../../hooks/useBonusEligibility';
import DisqualificationReasons from './DisqualificationReasons';

const QUALIFIED_BACKGROUND = 'var(--color-success-bg)';
const DISQUALIFIED_BACKGROUND = 'var(--color-danger-bg)';

const OuterDiv = styled.div<{ isOpen: boolean; isQualified: boolean }>`
  width: 100%;
  border-radius: 8px;
  padding: 0px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  border-width: 1px;
  border-style: solid;

  // state dependant - isQualified
  border-color: ${(props) =>
    props.isQualified
      ? 'var(--color-success-border)'
      : 'var(--color-danger-border)'};
  background-color: ${(props) =>
    props.isQualified ? QUALIFIED_BACKGROUND : DISQUALIFIED_BACKGROUND};
`;

const InnerDiv = styled.div<{ isQualified: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 0;

  border-top-width: 1px;
  border-top-style: solid;
  border-top-color: ${(props) =>
    props.isQualified
      ? 'var(--color-success-border)'
      : 'var(--color-danger-border)'};
`;

const OverrideBadge = ({
  persistedOverride,
  onRemoved,
  reasonCodes,
}: {
  persistedOverride: BonusTypes.Overrides.PersistedQualifier;
  onRemoved?: () => void;
  reasonCodes?: string[];
}) => {
  const label = useMemo(() => {
    if (persistedOverride.value) {
      return 'Qualified';
    }
    return 'Disqualified';
  }, [persistedOverride]);

  return (
    <div style={{ cursor: 'help' }}>
      <Tooltip
        content={
          persistedOverride.note ? (
            <OverrideTooltip
              {...persistedOverride.note}
              reasonCodes={reasonCodes}
            />
          ) : undefined
        }
      >
        <Badge badgeType={'Orange'} gap={4}>
          <Icon icon={'info'} />
          <Typography.Body type={'Button Text'}>
            {`Manually Set To: ${label}`}
          </Typography.Body>
          {!!onRemoved && (
            <Button
              size={'X-Small - Circle'}
              type={'Ghost'}
              onClick={onRemoved}
              icon={'cross'}
              iconColor={'var(--color-icon-secondary)'}
            />
          )}
        </Badge>
      </Tooltip>
    </div>
  );
};

const ClearBonusEligibilityOverride = ({
  bonusOverview,
  persistedOverride,
}: {
  bonusOverview: BonusTypes.BonusOverview;
  persistedOverride: BonusTypes.Overrides.PersistedQualifier;
}) => {
  const { setStagedOverrides, stagedOverrides } = bonusOverview;
  const isMarkedForRemoval = useMemo(() => {
    if (!stagedOverrides) {
      return false;
    }

    return (
      stagedOverrides.qualifier && stagedOverrides.qualifier.action === 'Clear'
    );
  }, [stagedOverrides]);

  const onRemoved = useCallback(() => {
    setStagedOverrides((current) => {
      if (!current) {
        return {
          criteria: [],
          qualifier: {
            action: 'Clear',
          },
        };
      }
      return {
        ...current,
        qualifier: {
          action: 'Clear',
        },
      };
    });
  }, [setStagedOverrides]);

  if (isMarkedForRemoval) {
    return (
      <Row style={{ gap: 8 }} centerAlign>
        <Badge badgeType={'Blue'} gap={4}>
          <Icon icon={'warning'} />
          <Typography.Body type={'Badge'}>Override Removed</Typography.Body>
        </Badge>
        <SetBonusEligibilityOverride bonusOverview={bonusOverview} />
      </Row>
    );
  }

  return (
    <OverrideBadge
      persistedOverride={persistedOverride}
      onRemoved={onRemoved}
      reasonCodes={bonusOverview.overrides?.reasonCodes}
    />
  );
};

const SetBonusEligibilityOverride = ({
  bonusOverview,
}: {
  bonusOverview: BonusTypes.BonusOverview;
}) => {
  const { isLoadingPreview } = useContext(BonusOverviewContext);
  const { rewardOutput, setStagedOverrides, stagedOverrides } = bonusOverview;
  const [isQualified, setIsQualified] = useState<boolean>(
    rewardOutput.eligibleForBonus === 'qualified',
  );

  // Keep state in sync with the RewardOutput
  // We don't use a memo here as we want to optimistically update this state
  useEffect(() => {
    setIsQualified(rewardOutput.eligibleForBonus === 'qualified');
  }, [rewardOutput.eligibleForBonus]);

  const isOverridden = useMemo(() => {
    return stagedOverrides && stagedOverrides.qualifier;
  }, [stagedOverrides]);

  const QualifiedLabel = useMemo(() => {
    return (
      <Row centerAlign style={{ gap: 8 }}>
        <Icon icon={'circleCheckmark'} color={'var(--color-status-success)'} />
        <Typography.Body type={'Button Text'}>Qualified</Typography.Body>
      </Row>
    );
  }, []);

  const DisqualifiedLabel = useMemo(() => {
    return (
      <Row centerAlign style={{ gap: 8 }}>
        <Icon icon={'warning'} color={'var(--color-status-danger)'} />
        <Typography.Body type={'Button Text'}>Disqualified</Typography.Body>
      </Row>
    );
  }, []);

  const renderSelectedLabel = useCallback(() => {
    if (rewardOutput.eligibleForBonus === 'qualified') {
      return QualifiedLabel;
    }

    return DisqualifiedLabel;
  }, [DisqualifiedLabel, QualifiedLabel, rewardOutput.eligibleForBonus]);

  const options = useMemo((): DropdownOption[] => {
    return [
      {
        key: 'qualified',
        label: 'Qualified',
        renderLabel: () => QualifiedLabel,
        isSelected: isQualified,
        onSelected: () => {
          const newValue: BonusTypes.Overrides.UserInputQualifier = {
            value: true,
            action: 'Set',
            note: '',
          };
          setStagedOverrides((current) => {
            if (current) {
              return {
                ...current,
                qualifier: newValue,
              };
            }
            return {
              qualifier: newValue,
              criteria: [],
            };
          });
        },
      },
      {
        key: 'disqualified',
        label: 'Disqualified',
        renderLabel: () => DisqualifiedLabel,
        isSelected: !isQualified,
        onSelected: () => {
          const newValue: BonusTypes.Overrides.UserInputQualifier = {
            value: false,
            action: 'Set',
            note: '',
          };
          setStagedOverrides((current) => {
            if (current) {
              return {
                ...current,
                qualifier: newValue,
                criteria: [], // clear other overrides
              };
            }
            return {
              qualifier: newValue,
              criteria: [],
            };
          });
        },
      },
    ];
  }, [DisqualifiedLabel, QualifiedLabel, isQualified, setStagedOverrides]);

  return (
    <Row centerAlign style={{ gap: 8 }}>
      {isOverridden && (
        <Tooltip content={`Overridden`}>
          <Icon icon={'info'} color={'var(--color-status-warning)'} />
        </Tooltip>
      )}
      <Dropdown
        testId={`set-eligibility-dropdown`}
        options={options}
        renderSelectedLabel={renderSelectedLabel}
        isDisabled={isLoadingPreview}
      />
    </Row>
  );
};

/**
 * When in override mode;
 * - If an override is persisted, give an option to clear it
 * - Otherwise - give an option to set an override
 *
 * When not in override mode;
 * - If there is a persisted override, show it
 */
const Overrides = () => {
  const { bonusOverview, currentMode } = useContext(BonusOverviewContext);
  if (!bonusOverview) {
    return null;
  }

  const persistedOverride: BonusTypes.Overrides.PersistedQualifier | undefined =
    bonusOverview.overrides
      ? bonusOverview.overrides.qualifierOverride || undefined
      : undefined;

  if (currentMode.mode !== 'override') {
    if (persistedOverride) {
      return (
        <OverrideBadge
          persistedOverride={persistedOverride}
          reasonCodes={bonusOverview.overrides?.reasonCodes}
        />
      );
    }
    return null;
  }

  if (persistedOverride) {
    return (
      <ClearBonusEligibilityOverride
        persistedOverride={persistedOverride}
        bonusOverview={bonusOverview}
      />
    );
  }

  return <SetBonusEligibilityOverride bonusOverview={bonusOverview} />;
};

const BonusEligibilityOverview = ({
  bonusProgram,
  rewardOutput,
}: {
  bonusProgram: BonusTypes.BonusProgram;
  rewardOutput: BonusTypes.Rewards.AnyDriverRewardsOutput;
}) => {
  const { isOpen, open, close } = usePopup();
  const { currentMode } = useContext(BonusOverviewContext);
  const { board } = useContext(BoardContext);
  const { isQualified, expectations, StatusIcon } = useBonusEligibility({
    bonusProgram,
    rewardOutput,
  });
  // Opt-in per board: the in-slideout override screen spells out which
  // requirements the driver is failing. Outside a board (entity-app screen)
  // BoardContext is empty, so this stays off.
  const isShowingReasons =
    currentMode.mode === 'override' &&
    !!board &&
    isPerformanceBoard(board) &&
    !!board.isDisqualificationReasonsEnabled;

  return (
    <>
      <OuterDiv
        isOpen={isOpen || currentMode.mode === 'override'}
        isQualified={isQualified}
      >
        <Row spaceBetween centerAlign style={{ height: 56 }}>
          <Row centerAlign style={{ gap: 8 }}>
            {StatusIcon}
            <Typography.Header type={'H5'}>
              {`Bonus Eligibility: ${isQualified ? 'Unlocked' : 'Disqualified'}`}
            </Typography.Header>
          </Row>
          <Row centerAlign style={{ gap: 8 }}>
            <Overrides />
            {currentMode.mode !== 'override' && (
              <Button
                size={'X-Small - Circle'}
                type={'Ghost'}
                onClick={isOpen ? close : open}
                icon={isOpen ? 'chevron-up' : 'chevron-down'}
                iconColor={'var(--color-icon-secondary)'}
              />
            )}
          </Row>
        </Row>

        {isShowingReasons && (
          <DisqualificationReasons
            expectations={expectations}
            isQualified={isQualified}
          />
        )}

        {isOpen && (
          <InnerDiv isQualified={isQualified}>
            {expectations.map((e) => (
              <Expectation
                key={e.key}
                label={e.label}
                isPassed={e.isPassed}
                actual={e.actual}
                target={e.target}
                variant={'overview'}
                renderRight={
                  currentMode.mode !== 'override' ? (
                    <RecordTaggingButton
                      taggableField={e.output}
                      fieldType={e.fieldType}
                    />
                  ) : undefined
                }
              />
            ))}
          </InnerDiv>
        )}
      </OuterDiv>
    </>
  );
};

const Gate = () => {
  const { isLoading, bonusOverview } = useContext(BonusOverviewContext);

  if (isLoading) {
    return (
      <Card style={{ height: 56 }}>
        <Loading />
      </Card>
    );
  }

  if (!bonusOverview) {
    return (
      <FlexCentered style={{ height: 56 }}>
        <Typography.Header type={'H4'}>
          Bonus information not found
        </Typography.Header>
      </FlexCentered>
    );
  }

  return (
    <BonusEligibilityOverview
      rewardOutput={bonusOverview.rewardOutput}
      bonusProgram={bonusOverview.bonusProgram}
    />
  );
};

export default Gate;
